import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {A360ProductCd} from 'Quoting/models/Quoting';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getUnderwritingPropertyPaths} from './PropertyPaths';
import {getSelfServeSupportedMaxNumberOfLives} from 'Quoting/utils/validationHelpers';

export const UnderwritingBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedProduct, selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? '';
  const underwritingPropertyPaths = getUnderwritingPropertyPaths(selectedPlan?.planIndex);
  const numberOfLives = getQuoteFieldValue<number>('participationDetails.numberOfEligibleLives');
  const currentGIAmount = getQuoteFieldValue<number>(underwritingPropertyPaths.indGIAmount);
  const giAmount = numberOfLives >= 250 && numberOfLives <= getSelfServeSupportedMaxNumberOfLives() 
    ? 30000 : currentGIAmount;

  useEffect(() => {
    if (selectedProduct?.code !== A360ProductCd.CI) return;
    if (currentGIAmount === giAmount) return;
    updateQuoteFieldValue(underwritingPropertyPaths.indGIAmount, giAmount);
  }, [giAmount, selectedProduct?.code, underwritingPropertyPaths.indGIAmount, updateQuoteFieldValue, currentGIAmount]);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'underwriting-row-1'},
      items: [
        {
          key: underwritingPropertyPaths.underwritingTypeCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Underwriting Type"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}Underwriting.underwritingTypeCd`}
              disabled
              value={getQuoteFieldValue<string>(underwritingPropertyPaths.underwritingTypeCd)}
              id={underwritingPropertyPaths.underwritingTypeCd}
              onChange={e =>
                updateQuoteFieldValue(underwritingPropertyPaths.underwritingTypeCd, e.target.value as string)
              }
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: underwritingPropertyPaths.indGIAmount,
          hidden: selectedProduct?.code !== A360ProductCd.CI,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Maximum Guaranteed Issue Amount - Participant"
              value={giAmount}
              id={underwritingPropertyPaths.indGIAmount}
              onChange={e => updateQuoteFieldValue(underwritingPropertyPaths.indGIAmount, parseInt(e.target.value))}
              disabled
              adornment="money"
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'underwriting-row-2'},
      items: [
        {
          key: underwritingPropertyPaths.isEOIRequired,
          hidden: selectedProduct?.code !== A360ProductCd.CI,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="switch"
              label="EOI Required for Additional Events"
              checked={getQuoteFieldValue<boolean>(underwritingPropertyPaths.isEOIRequired)}
              id={underwritingPropertyPaths.isEOIRequired}
              onChange={e => updateQuoteFieldValue(underwritingPropertyPaths.isEOIRequired, e.target.checked)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];
  return (
    <FormWrapper title="Underwriting" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
